import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Card, Button, Field, Input, Select } from "@/ui";
import ConfirmAction from "@/components/ConfirmAction";
import { triggerWords, rewardWords } from "./Deals";

// A row of plain-word choices (acts like radio buttons).
function Choices({ value, onChange, options, testid }) {
  return (
    <div className="row" style={{ gap: 8, flexWrap: "wrap" }} data-testid={testid}>
      {options.map((o) => (
        <Button key={o.value} size="sm" variant={value === o.value ? "primary" : "default"}
          data-testid={`${testid}-${o.value}`} onClick={() => onChange(o.value)}>{o.label}</Button>
      ))}
    </div>
  );
}

const toIso = (local) => (local ? new Date(local).toISOString() : null);
const fromIso = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

export default function DealBuilder() {
  const { dealId } = useParams();
  const editing = Boolean(dealId);
  const { shopId, shopName } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [triggerType, setTriggerType] = useState("any_order");
  const [minSpend, setMinSpend] = useState("");
  const [triggerChannel, setTriggerChannel] = useState("chat");
  const [productIds, setProductIds] = useState([]);
  const [rewardType, setRewardType] = useState("percent_off");
  const [percent, setPercent] = useState("10");
  const [amount, setAmount] = useState("");
  const [freeProductId, setFreeProductId] = useState("");
  const [upgradeAmount, setUpgradeAmount] = useState("");
  const [bundlePrice, setBundlePrice] = useState("");
  const [stackable, setStackable] = useState(false);
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [totalUses, setTotalUses] = useState("");
  const [perCustomer, setPerCustomer] = useState("");

  const [products, setProducts] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(editing);

  useEffect(() => {
    if (!shopId) return;
    api.get(`/products?shopId=${shopId}`).then((r) => setProducts(r.data.products)).catch(() => {});
    if (editing) {
      api.get(`/deals/${dealId}?shopId=${shopId}`).then((r) => {
        const d = r.data;
        setName(d.name || "");
        const t = d.trigger || {}; setTriggerType(t.type || "any_order");
        if (t.minSpendCentavos) setMinSpend(String(t.minSpendCentavos / 100));
        if (t.channel) setTriggerChannel(t.channel);
        if (t.productIds) setProductIds(t.productIds);
        const rw = d.reward || {}; setRewardType(rw.type || "percent_off");
        if (rw.type === "percent_off") setPercent(String(rw.value || 0));
        if (rw.type === "amount_off") setAmount(String((rw.value || 0) / 100));
        if (rw.type === "free_upgrade") setUpgradeAmount(String((rw.value || 0) / 100));
        if (rw.type === "bundle_price") setBundlePrice(String((rw.value || 0) / 100));
        if (rw.productId) setFreeProductId(rw.productId);
        setStackable(Boolean(d.stackable));
        setStartsAt(fromIso((d.when || {}).startsAt));
        setEndsAt(fromIso((d.when || {}).endsAt));
        const lim = d.limits || {};
        if (lim.totalUses != null) setTotalUses(String(lim.totalUses));
        if (lim.usesPerCustomer != null) setPerCustomer(String(lim.usesPerCustomer));
      }).catch((e) => setError(friendlyError(e))).finally(() => setLoading(false));
    }
  }, [shopId, dealId, editing]);

  const payload = useMemo(() => {
    const trigger = { type: triggerType };
    if (triggerType === "minimum_spend") trigger.minSpendCentavos = Math.round(Number(minSpend || 0) * 100);
    if (triggerType === "specific_channel") trigger.channel = triggerChannel;
    if (triggerType === "specific_products") trigger.productIds = productIds;
    const reward = { type: rewardType };
    if (rewardType === "percent_off") reward.value = parseInt(percent || "0", 10);
    if (rewardType === "amount_off") reward.value = Math.round(Number(amount || 0) * 100);
    if (rewardType === "free_upgrade") reward.value = Math.round(Number(upgradeAmount || 0) * 100);
    if (rewardType === "bundle_price") reward.value = Math.round(Number(bundlePrice || 0) * 100);
    if (rewardType === "free_item") reward.productId = freeProductId;
    return {
      shopId, name: name.trim(), trigger, reward,
      when: { startsAt: toIso(startsAt), endsAt: toIso(endsAt) },
      limits: {
        totalUses: totalUses === "" ? null : parseInt(totalUses, 10),
        usesPerCustomer: perCustomer === "" ? null : parseInt(perCustomer, 10),
      },
      stackable,
      active: true,
    };
  }, [shopId, name, triggerType, minSpend, triggerChannel, productIds, rewardType, percent, amount, upgradeAmount, bundlePrice, freeProductId, startsAt, endsAt, totalUses, perCustomer, stackable]);

  const toggleProduct = (id) => setProductIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const save = async () => {
    setBusy(true); setError("");
    try {
      if (editing) await api.put(`/deals/${dealId}`, payload);
      else await api.post("/deals", payload);
      navigate("/deals");
    } catch (e) { setError(friendlyError(e)); setBusy(false); setConfirm(false); }
  };

  const previewLines = [
    `Name: ${name || "(add a name)"}`,
    `Applies: ${triggerWords(payload.trigger)}`,
    `They get: ${rewardWords(payload.reward)}`,
    startsAt || endsAt ? `When: ${startsAt ? new Date(startsAt).toLocaleString() : "now"} to ${endsAt ? new Date(endsAt).toLocaleString() : "no end"}` : "When: runs anytime",
    `Limits: ${totalUses || "no"} total, ${perCustomer || "no"} per customer`,
  ];

  return (
    <Layout shopName={shopName}>
      <button className="btn btn-ghost btn-sm" onClick={() => navigate("/deals")} data-testid="deal-back" style={{ marginBottom: 12 }}>
        <ArrowLeft size={16} strokeWidth={1.75} /> back to deals
      </button>
      <h1 className="t-h1" style={{ marginBottom: 4 }}>{editing ? "Edit deal" : "New deal"}</h1>
      <p className="muted" style={{ marginBottom: 20 }}>Four questions, plain words. That's the whole deal.</p>

      {loading ? <Card><p className="muted">Loading…</p></Card> : (
        <div className="stack" style={{ gap: 16, maxWidth: 720 }}>
          <Card>
            <Field label="Give it a short name" htmlFor="deal-name">
              <Input id="deal-name" data-testid="deal-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Merienda Monday" />
            </Field>
          </Card>

          <Card>
            <h3 className="t-h3" style={{ marginBottom: 4 }}>1. What makes it apply?</h3>
            <p className="muted t-small" style={{ marginBottom: 12 }}>Pick when this deal should kick in.</p>
            <Choices value={triggerType} onChange={setTriggerType} testid="q-trigger" options={[
              { value: "any_order", label: "Any order" },
              { value: "specific_products", label: "Chosen items" },
              { value: "minimum_spend", label: "Minimum spend" },
              { value: "first_time_customer", label: "First-time customer" },
              { value: "specific_channel", label: "A channel" },
            ]} />
            {triggerType === "minimum_spend" && (
              <Field label="Spend at least (₱)" htmlFor="min-spend" style={{ marginTop: 12 }}>
                <Input id="min-spend" data-testid="min-spend" inputMode="decimal" value={minSpend} onChange={(e) => setMinSpend(e.target.value)} placeholder="500" />
              </Field>
            )}
            {triggerType === "specific_channel" && (
              <Field label="Which channel" htmlFor="trig-channel" style={{ marginTop: 12 }}>
                <Select id="trig-channel" data-testid="trig-channel" value={triggerChannel} onChange={(e) => setTriggerChannel(e.target.value)}>
                  <option value="chat">Chat</option>
                  <option value="shop_page">Shop page</option>
                  <option value="counter">Counter</option>
                </Select>
              </Field>
            )}
            {triggerType === "specific_products" && (
              <div style={{ marginTop: 12 }}>
                <p className="t-label" style={{ marginBottom: 8 }}>Which items</p>
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  {products.map((p) => (
                    <Button key={p.id} size="sm" variant={productIds.includes(p.id) ? "primary" : "default"}
                      data-testid={`trig-product-${p.id}`} onClick={() => toggleProduct(p.id)}>{p.name}</Button>
                  ))}
                </div>
              </div>
            )}
          </Card>

          <Card>
            <h3 className="t-h3" style={{ marginBottom: 4 }}>2. What do they get?</h3>
            <p className="muted t-small" style={{ marginBottom: 12 }}>Choose the reward.</p>
            <Choices value={rewardType} onChange={setRewardType} testid="q-reward" options={[
              { value: "percent_off", label: "Percent off" },
              { value: "amount_off", label: "Peso off" },
              { value: "free_item", label: "Free item" },
              { value: "cheapest_free", label: "Cheapest free (1+1)" },
              { value: "free_upgrade", label: "Free upgrade" },
              { value: "bundle_price", label: "Bundle price" },
            ]} />
            {rewardType === "percent_off" && (
              <Field label="How many percent off" htmlFor="rw-pct" style={{ marginTop: 12 }}>
                <Input id="rw-pct" data-testid="rw-pct" inputMode="numeric" value={percent} onChange={(e) => setPercent(e.target.value)} placeholder="10" />
              </Field>
            )}
            {rewardType === "amount_off" && (
              <Field label="How much off (₱)" htmlFor="rw-amt" style={{ marginTop: 12 }}>
                <Input id="rw-amt" data-testid="rw-amt" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="50" />
              </Field>
            )}
            {rewardType === "free_item" && (
              <Field label="Which item is free" htmlFor="rw-free" style={{ marginTop: 12 }}>
                <Select id="rw-free" data-testid="rw-free" value={freeProductId} onChange={(e) => setFreeProductId(e.target.value)}>
                  <option value="">Choose an item…</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Select>
              </Field>
            )}
            {rewardType === "free_upgrade" && (
              <Field label="Value of the upgrade (₱)" htmlFor="rw-up" style={{ marginTop: 12 }}>
                <Input id="rw-up" data-testid="rw-up" inputMode="decimal" value={upgradeAmount} onChange={(e) => setUpgradeAmount(e.target.value)} placeholder="30" />
              </Field>
            )}
            {rewardType === "bundle_price" && (
              <Field label="Bundle price (₱) — for the chosen items above" htmlFor="rw-bundle" style={{ marginTop: 12 }}>
                <Input id="rw-bundle" data-testid="rw-bundle" inputMode="decimal" value={bundlePrice} onChange={(e) => setBundlePrice(e.target.value)} placeholder="199" />
              </Field>
            )}
          </Card>

          <Card>
            <h3 className="t-h3" style={{ marginBottom: 4 }}>Can it combine with other deals?</h3>
            <p className="muted t-small" style={{ marginBottom: 12 }}>If off, only the single best deal applies to an order.</p>
            <Choices value={stackable ? "yes" : "no"} onChange={(v) => setStackable(v === "yes")} testid="q-stack" options={[
              { value: "no", label: "Only the best deal" },
              { value: "yes", label: "Can stack with others" },
            ]} />
          </Card>

          <Card>
            <h3 className="t-h3" style={{ marginBottom: 4 }}>3. When does it run?</h3>
            <p className="muted t-small" style={{ marginBottom: 12 }}>Leave blank to run anytime. Set an end for a flash sale.</p>
            <div className="grid grid-2" style={{ gap: 12 }}>
              <Field label="Starts" htmlFor="starts"><Input id="starts" data-testid="starts" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} /></Field>
              <Field label="Ends" htmlFor="ends"><Input id="ends" data-testid="ends" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} /></Field>
            </div>
          </Card>

          <Card>
            <h3 className="t-h3" style={{ marginBottom: 4 }}>4. How many times can it be used?</h3>
            <p className="muted t-small" style={{ marginBottom: 12 }}>Leave blank for no limit.</p>
            <div className="grid grid-2" style={{ gap: 12 }}>
              <Field label="Total uses" htmlFor="tot"><Input id="tot" data-testid="total-uses" inputMode="numeric" value={totalUses} onChange={(e) => setTotalUses(e.target.value)} placeholder="no limit" /></Field>
              <Field label="Per customer" htmlFor="perc"><Input id="perc" data-testid="per-customer" inputMode="numeric" value={perCustomer} onChange={(e) => setPerCustomer(e.target.value)} placeholder="no limit" /></Field>
            </div>
          </Card>

          {error ? <p className="err-text" data-testid="deal-error">{error}</p> : null}
          <div className="actions">
            <Button onClick={() => navigate("/deals")}>cancel</Button>
            <Button variant="primary" data-testid="save-deal" onClick={() => setConfirm(true)}>{editing ? "review changes" : "review deal"}</Button>
          </div>
        </div>
      )}

      <ConfirmAction
        open={confirm}
        title={editing ? "Save these changes?" : "Turn this deal on?"}
        preview={previewLines}
        confirmLabel="Yes, save it"
        busy={busy}
        error={error}
        onConfirm={save}
        onCancel={() => setConfirm(false)}
      />
    </Layout>
  );
}
